import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Info,
  Plus,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PAT } from "@/constants/testIds";
import { searchDiagnosisCodes } from "@/lib/diagnosisApi";
import { hydrateProblemList, promotePmhToProblemListRemote, readProblemList } from "@/lib/diagnosisSources";
import { catalogItemToDiagnosis } from "@/lib/encounterDiagnoses";
import {
  createMedicalHistory,
  listMedicalHistory,
  removeMedicalHistory,
  reviewMedicalHistory,
  updateMedicalHistory,
} from "@/lib/pmhApi";
import {
  PERTINENT_NEGATIVE_ITEMS,
  PMH_SOURCES,
  QUICK_ENTRY_ITEMS,
  conditionsFromItems,
  formatOnsetDate,
  formatPmhDiagnosis,
  formatReviewedAt,
  formatSearchDiagnosis,
  negativesFromItems,
  toPmhConditionPayload,
  toPmhNegativePayload,
} from "@/lib/pmhCatalog";
import "@/styles/emr-medical-history.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((row) => row.msg).join("; ");
  return "Unable to update Medical History.";
}

export default function MedicalHistoryWorkspace({
  patientId,
  birthDate,
  onClose,
  onPrevious,
  onNext,
  accessReadOnly = false,
}) {
  const [items, setItems] = useState([]);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [negativePickerOpen, setNegativePickerOpen] = useState(false);
  const [problemCodes, setProblemCodes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProblemCodes(readProblemList(patientId).map((row) => row.code));
    Promise.resolve(hydrateProblemList(patientId) || []).then((items) => {
      if (!cancelled) setProblemCodes((items || []).map((row) => row.code));
    });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listMedicalHistory(patientId)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items || []);
        setReview(data.review || null);
      })
      .catch((error) => {
        if (cancelled) return;
        setItems([]);
        setReview(null);
        setLoadError(apiErrorMessage(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useEffect(() => {
    if (!debounced) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    searchDiagnosisCodes({ q: debounced, active: true, limit: 25 })
      .then((data) => {
        if (cancelled) return;
        setResults((data.items || []).map(catalogItemToDiagnosis).filter((row) => row.code));
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const conditions = useMemo(() => conditionsFromItems(items), [items]);
  const negatives = useMemo(() => negativesFromItems(items), [items]);

  async function addCondition(item) {
    if (!item?.code || saving) return;
    setSaving(true);
    try {
      const created = await createMedicalHistory(patientId, toPmhConditionPayload(item));
      setItems((current) => [...current.filter((row) => row.id !== created.id), created]);
      setSelected(null);
      toast.success("Added to Medical History");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  async function addNegative(item) {
    if (!item?.description || saving) return;
    setSaving(true);
    setNegativePickerOpen(false);
    try {
      const created = await createMedicalHistory(patientId, toPmhNegativePayload(item));
      setItems((current) => [...current.filter((row) => row.id !== created.id), created]);
      toast.success("Pertinent negative recorded");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  }

  async function patchItem(itemId, payload) {
    try {
      const updated = await updateMedicalHistory(itemId, payload);
      setItems((current) => current.map((row) => (row.id === itemId ? updated : row)));
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  }

  async function removeItem(itemId) {
    try {
      await removeMedicalHistory(itemId);
      setItems((current) => current.filter((row) => row.id !== itemId));
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  }

  async function promote(item) {
    try {
      const next = (await promotePmhToProblemListRemote(patientId, item)) || [];
      setProblemCodes(next.map((row) => row.code));
      toast.success("Added to Problem List");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  }

  async function markReviewed() {
    try {
      const next = await reviewMedicalHistory(patientId);
      setReview(next);
      toast.success("Medical History marked as reviewed");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    }
  }

  return (
    <div data-testid={PAT.chartPmh} className="medical-history-section space-y-4">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
        <section className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="mb-3 flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-[#1E79E8]" />
            <h2 className="font-semibold text-slate-900">Medical History</h2>
          </div>

          {accessReadOnly ? null : (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                type="search"
                autoComplete="off"
                spellCheck={false}
                data-testid={PAT.chartPmhSearch}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSelected(null);
                }}
                placeholder="Search or enter medical history (use ICD-10)"
                className="browser-default medical-history-search bg-white pl-10"
              />
            </div>
            <Button
              type="button"
              data-testid={PAT.chartPmhAdd}
              onClick={() => addCondition(selected)}
              disabled={!selected || saving}
              className="medical-history-toolbar-btn bg-[#1E79E8] hover:bg-[#1869CC]"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid={PAT.chartPmhAddNegative}
              onClick={() => setNegativePickerOpen((open) => !open)}
              className="medical-history-toolbar-btn border-[#1E79E8]/40 text-[#1E79E8] hover:bg-[#E5F0FF]"
            >
              <Plus className="h-4 w-4" />
              Pertinent Negative
            </Button>
          </div>
          )}

          {accessReadOnly ? null : negativePickerOpen && (
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
              {PERTINENT_NEGATIVE_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-white"
                  onClick={() => addNegative(item)}
                >
                  {item.description}
                </button>
              ))}
            </div>
          )}

          {(searching || results.length > 0) && !accessReadOnly && (
            <ul
              data-testid={PAT.chartPmhResults}
              className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white"
            >
              {searching && (
                <li className="px-3 py-2 text-sm text-slate-500">Searching ICD-10…</li>
              )}
              {results.map((item) => {
                const active = selected?.code === item.code;
                return (
                  <li key={item.code}>
                    <button
                      type="button"
                      data-testid={`${PAT.chartPmhResult}-${item.code}`}
                      onClick={() => setSelected(item)}
                      className={`block w-full px-3 py-2 text-left text-sm ${
                        active ? "bg-[#E5F0FF] text-[#1E79E8]" : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {formatSearchDiagnosis(item)}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {loadError && (
            <p className="mt-3 text-sm text-amber-700">{loadError}</p>
          )}

          <div className="mt-5">
            <h3 className="mb-2 font-semibold text-[#1E79E8]">Past Medical History</h3>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table data-testid={PAT.chartPmhTable} className="medical-history-table w-full min-w-[720px]">
                <thead className="bg-slate-50 text-left font-semibold text-slate-500">
                  <tr>
                    <th>Diagnosis</th>
                    <th>Date</th>
                    <th>Comment</th>
                    <th>Source</th>
                    <th>PL</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && conditions.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-slate-400">
                        No past medical history recorded for this patient.
                      </td>
                    </tr>
                  )}
                  {conditions.map((item) => {
                    const onProblemList = problemCodes.includes(item.code);
                    const onsetLabel = formatOnsetDate(item.onsetDate, birthDate);
                    return (
                      <tr
                        key={item.id}
                        data-testid={`${PAT.chartPmhRow}-${item.code}`}
                        className="border-t border-slate-100"
                      >
                        <td className="font-medium text-[#1E79E8]">
                          {formatPmhDiagnosis(item)}
                        </td>
                        <td>
                          <Input
                            type="text"
                            value={item.onsetDate || ""}
                            placeholder="YYYY or date"
                            title={onsetLabel}
                            className="browser-default medical-history-control w-36 bg-white"
                            onChange={(event) => {
                              const value = event.target.value;
                              setItems((current) =>
                                current.map((row) =>
                                  row.id === item.id ? { ...row, onsetDate: value } : row,
                                ),
                              );
                            }}
                            onBlur={(event) =>
                              patchItem(item.id, { onsetDate: event.target.value || null })
                            }
                            aria-label={`Date for ${item.code}`}
                          />
                          <span className="medical-history-onset-meta">{onsetLabel}</span>
                        </td>
                        <td>
                          <Input
                            type="text"
                            value={item.comment || ""}
                            placeholder="—"
                            className="browser-default medical-history-control bg-white"
                            onChange={(event) => {
                              const value = event.target.value;
                              setItems((current) =>
                                current.map((row) =>
                                  row.id === item.id ? { ...row, comment: value } : row,
                                ),
                              );
                            }}
                            onBlur={(event) =>
                              patchItem(item.id, { comment: event.target.value || null })
                            }
                            aria-label={`Comment for ${item.code}`}
                          />
                        </td>
                        <td>
                          <select
                            className="browser-default medical-history-control"
                            value={item.source || "patient"}
                            onChange={(event) => patchItem(item.id, { source: event.target.value })}
                            aria-label={`Source for ${item.code}`}
                          >
                            {PMH_SOURCES.map((source) => (
                              <option key={source.id} value={source.id}>
                                {source.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {onProblemList ? (
                            <span className="font-medium text-slate-500">On PL</span>
                          ) : accessReadOnly ? null : (
                            <button
                              type="button"
                              data-testid={`${PAT.chartPmhPromote}-${item.code}`}
                              onClick={() => promote(item)}
                              className="medical-history-pl-btn hover:bg-[#E5F0FF]"
                              aria-label={`Add ${item.code} to Problem List`}
                              title="Add this item to the patient's Problem List"
                            >
                              <Plus />
                            </button>
                          )}
                        </td>
                        <td className="text-right">
                          {accessReadOnly ? null : (
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            className="font-medium text-slate-500 hover:text-slate-800"
                          >
                            Remove
                          </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {negatives.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 font-semibold text-slate-700">Pertinent Negatives</h3>
              <ul className="space-y-1 text-slate-700">
                {negatives.map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-3">
                    <span>{item.description}</span>
                    {accessReadOnly ? null : (
                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      className="text-xs text-slate-500 hover:text-slate-800"
                    >
                      Remove
                    </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              data-testid={PAT.chartPmhReview}
              onClick={markReviewed}
              className="border-[#1E79E8]/40 text-[#1E79E8]"
              disabled={accessReadOnly}
            >
              <Check className="h-4 w-4" />
              Mark as Reviewed
            </Button>
            <p data-testid={PAT.chartPmhLastReviewed} className="text-slate-500">
              {review
                ? `Last Reviewed by ${review.reviewedByDisplay || "clinician"} on ${formatReviewedAt(review.reviewedAt)}`
                : "Not yet reviewed"}
            </p>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid={PAT.chartPmhRestore}
              disabled
              className="text-slate-400"
            >
              <RotateCcw className="h-4 w-4" />
              Restore
            </Button>
            <Button type="button" variant="outline" data-testid={PAT.chartPmhClose} onClick={onClose}>
              <X className="h-4 w-4" />
              Close
            </Button>
            <Button type="button" variant="outline" data-testid={PAT.chartPmhPrevious} onClick={onPrevious}>
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              type="button"
              data-testid={PAT.chartPmhNext}
              onClick={onNext}
              className="bg-[#1E79E8] hover:bg-[#1869CC]"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </section>

        <aside className="space-y-4">
          <section
            data-testid={PAT.chartPmhQuickEntry}
            className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-4"
          >
            <h3 className="mb-3 font-semibold text-slate-900">Quick Entry</h3>
            <div className="flex flex-wrap gap-2">
              {QUICK_ENTRY_ITEMS.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  data-testid={`${PAT.chartPmhQuickChip}-${item.code}`}
                  onClick={() => addCondition(item)}
                  className="medical-history-chip rounded-full bg-slate-100 font-medium text-slate-700 hover:bg-[#E5F0FF] hover:text-[#1E79E8]"
                >
                  {item.chip}
                </button>
              ))}
            </div>
          </section>

          <section
            data-testid={PAT.chartPmhNegatives}
            className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-4"
          >
            <h3 className="mb-3 font-semibold text-slate-900">Pertinent Negative</h3>
            <ul className="medical-history-side-list space-y-2">
              {PERTINENT_NEGATIVE_ITEMS.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-2 text-slate-700">
                  <span>{item.description}</span>
                  <button
                    type="button"
                    data-testid={`${PAT.chartPmhNegativeAdd}-${item.id}`}
                    onClick={() => addNegative(item)}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[#1E79E8] hover:bg-[#E5F0FF]"
                    aria-label={`Add ${item.description}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section
            data-testid={PAT.chartPmhLegend}
            className="rounded-xl border border-slate-200 bg-[#FFFFFF] p-4"
          >
            <h3 className="mb-3 font-semibold text-slate-900">Source (Src.) Legend</h3>
            <ul className="medical-history-side-list space-y-1 text-slate-600">
              {PMH_SOURCES.map((source) => (
                <li key={source.id}>
                  <span className="font-medium text-slate-800">{source.label}</span>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>

      <div className="rounded-lg border border-[#1E79E8]/20 bg-[#E5F0FF] px-3 py-2 text-[#1E79E8]">
        <Info className="mr-1 inline h-4 w-4" />
        PL (+) = Add this item to the patient&apos;s Problem List.
      </div>
    </div>
  );
}
